<template>
  <div class="tab">
    <!-- Condiciones de cierre: siempre visibles, se cumplan o no. Es la
         pregunta que más se hace en esta pantalla. -->
    <div class="bloque">
      <div class="bloque-titulo">Condiciones de cierre (RN-08/09)</div>
      <ul class="checks">
        <li v-for="(c, i) in checks" :key="i" :class="{ ok: c.ok }">
          <PortalIcon :name="c.ok ? 'CheckCircle2' : 'AlertTriangle'" :size="14" />
          {{ c.texto }}
        </li>
      </ul>
      <p class="nota">
        El cierre lo ejecuta Customer Experience, nunca el responsable del área.
      </p>
    </div>

    <!-- Resultado del cierre, cuando ya ocurrió -->
    <div v-if="plan.resultadoObtenido" class="bloque">
      <div class="bloque-titulo">Resultado</div>
      <p class="texto">{{ plan.resultadoObtenido }}</p>
      <div v-if="plan.leccionesAprendidas" class="sub">
        <span class="sub-lbl">Lecciones aprendidas</span>
        <p class="texto">{{ plan.leccionesAprendidas }}</p>
      </div>
      <div v-if="plan.nivelEfectividad" class="sub">
        <span class="sub-lbl">Efectividad</span>
        <PortalBadge
          :text="etiquetaEfectividad(plan.nivelEfectividad)"
          :color="colorEfectividad(plan.nivelEfectividad)"
        />
      </div>
    </div>

    <div v-if="plan.motivoReapertura" class="bloque reapertura">
      <div class="bloque-titulo">Motivo de reapertura (RN-10)</div>
      <p class="texto">{{ plan.motivoReapertura }}</p>
    </div>

    <!-- Historial de validaciones -->
    <div class="bloque">
      <div class="bloque-titulo">Validaciones de CX</div>
      <ul v-if="plan.validaciones.length" class="lista">
        <li v-for="(v, i) in plan.validaciones" :key="i" class="item">
          <div class="item-cab">
            <PortalBadge
              :text="etiquetaResultado(v.resultado)"
              :color="colorResultado(v.resultado)"
            />
            <span class="meta">{{ v.validador || "—" }} · {{ v.fecha }}</span>
          </div>
          <p v-if="v.comentarios" class="texto">{{ v.comentarios }}</p>
        </li>
      </ul>
      <p v-else class="vacio">Sin validaciones registradas.</p>
    </div>

    <!-- Cambios de fecha (RN-05/06/07) -->
    <div class="bloque">
      <div class="bloque-titulo">Solicitudes de cambio de fecha</div>
      <ul v-if="plan.cambiosFecha.length" class="lista">
        <li v-for="c in plan.cambiosFecha" :key="c.idCambioFecha" class="item">
          <div class="item-cab">
            <span class="fechas">
              {{ fmtFecha(c.fechaActual) }} → <strong>{{ fmtFecha(c.fechaPropuesta) }}</strong>
            </span>
            <PortalBadge
              :text="etiquetaSolicitud(c.estatus)"
              :color="colorSolicitud(c.estatus)"
            />
          </div>
          <div class="meta">Solicitó: {{ c.solicita || "—" }}</div>
          <p class="texto">{{ c.motivo }}</p>

          <div v-if="c.estatus === 'pendiente' && puedeAprobar" class="acciones">
            <button class="btn aprobar" @click="$emit('aprobar-fecha', c)">
              Aprobar cambio
            </button>
            <button class="btn rechazar" @click="$emit('rechazar-fecha', c)">
              Rechazar
            </button>
          </div>
        </li>
      </ul>
      <p v-else class="vacio">Ninguna. La fecha compromiso no se ha movido.</p>
    </div>
  </div>
</template>

<script>
import PortalIcon from "../PortalIcon.vue";
import PortalBadge from "../PortalBadge.vue";
import { fmtFecha, puedeCerrar } from "../cx.rules";

export default {
  name: "TabValidacion",
  components: { PortalIcon, PortalBadge },
  props: {
    plan: { type: Object, required: true },
    // Solo el gerente (o CX/admin) resuelve solicitudes de cambio de fecha.
    puedeAprobar: { type: Boolean, default: false },
  },
  emits: ["aprobar-fecha", "rechazar-fecha"],
  computed: {
    // Se listan las cuatro condiciones, marcando cuáles ya se cumplen. Es más
    // útil que mostrar solo lo que falta: el usuario ve cuánto le queda.
    checks() {
      const r = puedeCerrar(this.plan);
      const todas = [
        "El avance debe ser 100%",
        "Hay actividades pendientes",
        "Se requiere al menos una evidencia aprobada",
        "El plan debe estar en validación",
      ];
      const textos = {
        "El avance debe ser 100%": "Avance al 100%",
        "Hay actividades pendientes": "Todas las actividades concluidas",
        "Se requiere al menos una evidencia aprobada": "Al menos una evidencia aprobada",
        "El plan debe estar en validación": "El plan está en validación",
      };
      return todas.map((k) => ({
        texto: textos[k],
        ok: r.faltan.indexOf(k) === -1,
      }));
    },
  },
  methods: {
    fmtFecha,
    etiquetaResultado: (r) =>
      ({ aprobada: "Aprobada", rechazada: "Rechazada", observaciones: "Con observaciones" }[r] || r),
    colorResultado: (r) =>
      ({ aprobada: "#16a34a", rechazada: "#dc2626", observaciones: "#d97706" }[r] || "#64748b"),
    etiquetaSolicitud: (s) =>
      ({ pendiente: "Pendiente", aprobada: "Aprobada", rechazada: "Rechazada" }[s] || s),
    colorSolicitud: (s) =>
      ({ pendiente: "#d97706", aprobada: "#16a34a", rechazada: "#dc2626" }[s] || "#64748b"),
    etiquetaEfectividad: (e) =>
      ({ efectiva: "Efectiva", parcial: "Parcial", no_efectiva: "No efectiva" }[e] || e),
    colorEfectividad: (e) =>
      ({ efectiva: "#16a34a", parcial: "#d97706", no_efectiva: "#dc2626" }[e] || "#64748b"),
  },
};
</script>

<style scoped>
.tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.bloque {
  border: 1px solid var(--pt-border);
  border-radius: 12px;
  padding: 14px;
}
.reapertura {
  border-color: #fca5a5;
}
.bloque-titulo {
  font-size: 12px;
  font-weight: 700;
  color: var(--pt-dim);
  text-transform: uppercase;
  margin-bottom: 10px;
}
.checks {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.checks li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #b45309;
}
.checks li.ok {
  color: #16a34a;
}
.nota {
  font-size: 11px;
  color: var(--pt-muted);
  margin: 10px 0 0;
}
.lista {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.item {
  border-left: 3px solid var(--pt-border);
  padding-left: 10px;
}
.item-cab {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.fechas {
  font-size: 13px;
}
.meta {
  font-size: 11px;
  color: var(--pt-muted);
  margin-top: 2px;
}
.texto {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--pt-dim);
  white-space: pre-wrap;
}
.sub {
  margin-top: 10px;
}
.sub-lbl {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--pt-muted);
  text-transform: uppercase;
  margin-bottom: 2px;
}
.vacio {
  font-size: 13px;
  color: var(--pt-muted);
  margin: 0;
}
.acciones {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.btn {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: 0;
  cursor: pointer;
  color: #fff;
  font-family: inherit;
}
.aprobar {
  background: #16a34a;
}
.rechazar {
  background: #dc2626;
}
</style>
